// src/oura/components/ActivityLog.tsx
//
// Everything that happened on this business, newest first
// (ourteam-backend#4508, #4531).
//
// The log existed before this screen did and nothing wrote to it automatically,
// so it only ever held what somebody remembered to type — while the platform
// silently knew most of it already. #4531 changed that: Oura now records its
// own drafts, publishes, reverts, page removals, completed runs and every check
// that reached zero. Those entries belong to the BUSINESS rather than to a
// task, so the per-task delivery record in the fix drawer could never show
// them, and until this screen they were written and never read.
//
// Two things this deliberately is not.
//
// It is not the weekly client update. That artifact is a document with a
// reference number, a rationale citing Search Console figures and an approval
// checklist, and it may well stay a skill rather than become a product surface.
// `/activity/summary` returns the MATERIAL such a document is assembled from,
// which is what the counts below are; turning entries into client-facing
// sentences is the writer's job.
//
// It is not scoped to one assessment. A re-scan mints a new assessment and new
// task ids, so a log filtered to the current run would lose everything recorded
// under the previous plan — which is "survives a re-assessment" in name only.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
    HistoryIcon,
    Loader2Icon,
    SparklesIcon,
    UserIcon,
    ZapIcon,
    AlertTriangleIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { getActivitySummary, getPlanActivity, type AeoActivityEntry, type AeoActivitySummary } from "@service/aeo";
import { Card, CardBody, CardHeader } from "../primitives/Card";
import { EmptyState } from "../primitives/EmptyState";
import { discoveredLabel } from "../lib/oura";
import { cn } from "../lib/cn";

type Outcome = AeoActivityEntry["outcome"];

const OUTCOME: Record<Outcome, { label: string; chip: string }> = {
    done: { label: "Done", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
    started: { label: "Started", chip: "border-blue-200 bg-blue-50 text-blue-700" },
    blocked: { label: "Blocked", chip: "border-amber-200 bg-amber-50 text-amber-700" },
    open: { label: "Open", chip: "border-gray-200 bg-gray-100 text-gray-600" },
    note: { label: "Note", chip: "border-gray-200 bg-gray-100 text-gray-600" },
};

/** Who wrote it. A LABEL only: Claude-in-Chrome calls the API as the signed-in
 *  user, so the backend cannot verify this and nothing here may depend on it.
 *
 *  `oura` means the platform recorded its own action. It stays `oura` even when
 *  a person clicked the button that caused it, because attributing the write to
 *  the human would make the log claim they typed a record they never saw. */
const AUTHOR: Record<AeoActivityEntry["author"]["kind"], { icon: LucideIcon; label: string }> = {
    claude: { icon: SparklesIcon, label: "Claude" },
    user: { icon: UserIcon, label: "You" },
    oura: { icon: ZapIcon, label: "Oura" },
};

/**
 * The filters, as the questions they answer.
 *
 * `outcome` is a server-side query for a reason the ticket states plainly:
 * "what closed this week" and "what is blocked" have to be separable BY QUERY
 * rather than by reading prose. So these are not client-side chips over a
 * fetched list — each one is a request, and "Needs a decision" is the amber
 * block of the weekly update expressed as a filter.
 */
const FILTERS: Array<{ key: string; label: string; outcome?: Outcome[] }> = [
    { key: "all", label: "Everything" },
    { key: "closed", label: "Closed", outcome: ["done"] },
    { key: "blocked", label: "Needs a decision", outcome: ["blocked"] },
    { key: "in_flight", label: "In flight", outcome: ["started"] },
];

export function ActivityLog({ assessmentId }: { assessmentId?: string }) {
    /**
     * Three states, not two.
     *
     * An empty list and a failed load must not look the same here. This is the
     * record of what work was done for a client, and rendering a 500 as "no
     * activity yet" reports that nothing happened — the one lie a log must
     * never tell.
     */
    type Load =
        | { kind: "loading" }
        | { kind: "ready"; entries: AeoActivityEntry[] }
        | { kind: "error" };
    const [load, setLoad] = useState<Load>({ kind: "loading" });
    const [filter, setFilter] = useState("all");
    // #3517: the log is what a weekly client update is written FROM, and without
    // a range that meant scrolling and reading to answer "what happened between
    // the 18th and the 25th". The service already accepted from/to; only the
    // caller never sent them.
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [summary, setSummary] = useState<AeoActivitySummary | null>(null);
    const [summaryState, setSummaryState] = useState<"idle" | "loading" | "error">("idle");

    const active = useMemo(() => FILTERS.find((f) => f.key === filter) ?? FILTERS[0], [filter]);

    const fetchEntries = useCallback(async () => {
        if (!assessmentId) {
            setLoad({ kind: "ready", entries: [] });
            return;
        }
        setLoad({ kind: "loading" });
        // Range and outcome compose into ONE request — "closed, between the 18th
        // and the 25th" is a single question, and filtering the second one in the
        // browser would silently disagree with the counts the summary reports.
        const rows = await getPlanActivity(assessmentId, {
            outcome: active.outcome,
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
        });
        setLoad(rows === null ? { kind: "error" } : { kind: "ready", entries: rows });
    }, [assessmentId, active.outcome, from, to]);

    const generateSummary = useCallback(async () => {
        if (!assessmentId || summaryState === "loading") return;
        setSummaryState("loading");
        const result = await getActivitySummary(assessmentId, {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
        });
        // null is a FAILED request, not an empty range — the two must not look
        // the same, or a broken report reads as "nothing happened this week".
        if (result === null) {
            setSummary(null);
            setSummaryState("error");
            return;
        }
        setSummary(result);
        setSummaryState("idle");
    }, [assessmentId, from, to, summaryState]);

    useEffect(() => {
        void fetchEntries();
    }, [fetchEntries]);

    return (
        <Card>
            <CardHeader
                icon={HistoryIcon}
                title="Activity"
                description="Everything Oura and your team have done on this site, newest first. It follows the business, so a re-scan does not start it over."
                // The row under the description, not `aside`: the header never
                // lets `aside` shrink, so beside the title this toolbar pushed
                // the page sideways on a phone. Here it has the card's width
                // and wraps, the pills under the dates and the button.
                meta={
                    <div className="flex flex-wrap items-center gap-2">
                        {/* #3517: From/To beside the outcome pills, so the two read
                            as one question rather than two competing filters. */}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                            <input
                                type="date"
                                aria-label="From date"
                                value={from}
                                max={to || undefined}
                                onChange={(e) => setFrom(e.target.value)}
                                className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700"
                            />
                            <span>to</span>
                            <input
                                type="date"
                                aria-label="To date"
                                value={to}
                                min={from || undefined}
                                onChange={(e) => setTo(e.target.value)}
                                className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700"
                            />
                            {(from || to) && (
                                <button
                                    type="button"
                                    onClick={() => { setFrom(""); setTo(""); setSummary(null); setSummaryState("idle"); }}
                                    className="px-1 text-xs font-medium text-blue-600 underline-offset-4 hover:underline"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => void generateSummary()}
                            disabled={summaryState === "loading"}
                            className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                            {summaryState === "loading" ? "Building…" : "Generate progress update"}
                        </button>
                        <div className="flex flex-wrap gap-1">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                type="button"
                                onClick={() => setFilter(f.key)}
                                className={cn(
                                    // House pill (skill §6b): active is solid blue, not
                                    // near-black — matches SegmentedControl everywhere else.
                                    "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                                    filter === f.key
                                        ? "bg-blue-600 text-white"
                                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                                )}
                            >
                                {f.label}
                            </button>
                        ))}
                        </div>
                    </div>
                }
            />
            <CardBody className="p-0">
                {/* The progress update, above the log it was built from. Counts
                    first because they are what a client update opens with; the
                    entries stay below as the evidence for them. */}
                {summaryState === "error" && (
                    <p className="border-b border-gray-100 px-4 py-3 text-sm text-rose-700">
                        The progress update could not be built. Please try again.
                    </p>
                )}
                {summary && summaryState !== "error" && (
                    <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                        <div className="flex items-baseline justify-between gap-3">
                            <p className="text-sm font-semibold text-gray-900">
                                Progress update
                                {summary.range.from || summary.range.to ? (
                                    <span className="ml-2 text-xs font-normal text-gray-500">
                                        {summary.range.from ?? "start"} to {summary.range.to ?? "today"}
                                    </span>
                                ) : null}
                            </p>
                            <button
                                type="button"
                                onClick={() => setSummary(null)}
                                className="text-xs font-medium text-gray-500 underline-offset-4 hover:underline"
                            >
                                Hide
                            </button>
                        </div>
                        <ul className="mt-2 space-y-0.5 text-sm text-gray-700">
                            <li>{summary.counts.closed} closed</li>
                            <li>{summary.counts.inFlight} in flight</li>
                            <li>{summary.counts.blocked} blocked</li>
                            {/* Its own line, not folded into "blocked": this is the
                                one group nobody on our side can move. */}
                            {summary.needsClientDecision.length > 0 && (
                                <li className="font-medium text-amber-800">
                                    {summary.needsClientDecision.length} waiting on the client to decide
                                </li>
                            )}
                        </ul>
                    </div>
                )}
                {load.kind === "loading" ? (
                    <div className="flex justify-center py-16">
                        <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                ) : load.kind === "error" ? (
                    <div className="px-4 py-10">
                        <EmptyState
                            icon={AlertTriangleIcon}
                            title="Could not load the activity"
                            description="The record is there; we could not read it just now. This is not a report that nothing happened."
                            action={
                                <button
                                    onClick={() => void fetchEntries()}
                                    className="text-sm font-medium text-blue-600 underline-offset-4 hover:underline"
                                >
                                    Try again
                                </button>
                            }
                        />
                    </div>
                ) : load.entries.length === 0 ? (
                    <div className="px-4 py-10">
                        <EmptyState
                            icon={HistoryIcon}
                            title={filter === "all" ? "Nothing recorded yet" : "Nothing here"}
                            description={
                                filter === "all"
                                    ? "Oura records its own scans, drafts, publishes and removals here as they happen, alongside anything your team writes against a task."
                                    : "Nothing matches this filter. Try Everything."
                            }
                        />
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {load.entries.map((entry) => {
                            const author = AUTHOR[entry.author.kind] ?? AUTHOR.user;
                            const AuthorIcon = author.icon;
                            const outcome = OUTCOME[entry.outcome] ?? OUTCOME.note;
                            return (
                                <div key={entry.id} className="flex items-start gap-3 px-4 py-3">
                                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                                        <AuthorIcon className="h-3.5 w-3.5 text-gray-500" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm leading-relaxed text-gray-800">{entry.body}</p>
                                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                                            <span>{entry.author.label || author.label}</span>
                                            <span>·</span>
                                            <span>{discoveredLabel(entry.at)}</span>
                                            {entry.week !== null && (
                                                <>
                                                    <span>·</span>
                                                    <span>{entry.week === 0 ? "Kickoff" : `Week ${entry.week}`}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <span
                                        className={cn(
                                            "mt-0.5 flex-shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                                            outcome.chip,
                                        )}
                                    >
                                        {outcome.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
